import type { CSSProperties, ReactNode } from 'react';
import {
  OBJETS_DE_BULLE,
  type BulleColoree,
  type CouleurDeBulle,
  type FormeDeBulle,
  type FormeDeProjet,
  type ObjetDeBulle,
  type TraitDeProjet,
} from '@nextilt/commun';

/**
 * Les quatorze bulles-objets, en silhouette (chantier habit-des-bulles, modification 02), recopiées
 * de la maquette `.scratch/chantiers/themes/maquettes/formes-de-bulle.html` (`FORMES`) : la taille
 * du dessin (`largeur` × `hauteur`, en pixels à l'échelle 1), le dessin lui-même, et la `zone` où
 * s'écrit le texte (haut, droite, bas, gauche).
 *
 * Les parties du dessin gardent les classes de la maquette, dessinées par
 * `styles/bulles/objets.css` : `tr` le contour plein (le papier de la bulle, son bord), `ti` une
 * partie teintée, `pl` une partie pleine, `ln` un trait seul, `lf` une ligne fine. Le mât du
 * drapeau et les anneaux du calendrier sont à l'encre (`encre`), à l'épaisseur de la maquette.
 */
export interface ObjetDessine {
  largeur: number;
  hauteur: number;
  zone: readonly [number, number, number, number];
  dessin: ReactNode;
}

export const OBJETS: Readonly<Record<ObjetDeBulle, ObjetDessine>> = {
  ampoule: {
    largeur: 170,
    hauteur: 160,
    zone: [32, 40, 62, 40],
    dessin: (
      <>
        <path className="ln" d="M85 2 v8M28 20 l8 8M142 20 l-8 8M8 64 h10M152 64 h10" />
        <path className="tr" d="M57 110 C 28 94, 24 62, 34 42 C 48 12, 122 12, 136 42 C 146 62, 142 94, 113 110 Z" />
        <path className="ti" d="M58 110 h54 v10 q0 5 -5 5 h-44 q-5 0 -5 -5 Z" />
        <path className="ln" d="M64 134 h42M70 145 h30M78 155 h14" />
      </>
    ),
  },
  telephone: {
    largeur: 128,
    hauteur: 184,
    zone: [28, 18, 36, 18],
    dessin: (
      <>
        <rect className="tr" x="8" y="4" width="112" height="176" rx="18" />
        <rect className="ti" x="17" y="24" width="94" height="126" rx="5" />
        <path className="ln" d="M52 14 h24" />
        <circle className="ln" cx="64" cy="165" r="6" />
      </>
    ),
  },
  ordinateur: {
    largeur: 196,
    hauteur: 156,
    zone: [18, 20, 54, 20],
    dessin: (
      <>
        <rect className="tr" x="6" y="4" width="184" height="110" rx="9" />
        <rect className="ti" x="16" y="14" width="164" height="90" rx="3" />
        <path className="tr" d="M84 114 l-6 22 h40 l-6 -22 Z" />
        <path className="tr" d="M56 138 h84 q8 0 8 8 v2 h-100 v-2 q0 -8 8 -8 Z" />
      </>
    ),
  },
  dossier: {
    largeur: 188,
    hauteur: 128,
    zone: [44, 16, 12, 16],
    dessin: (
      <>
        <path className="ti" d="M6 30 v-14 q0 -8 8 -8 h46 l12 12 h102 q8 0 8 8 v6 Z" />
        <path className="tr" d="M6 30 h176 q6 0 6 6 v78 q0 8 -8 8 h-166 q-8 0 -8 -8 Z" />
        <path className="lf" d="M18 42 h152" />
      </>
    ),
  },
  fichier: {
    largeur: 144,
    hauteur: 168,
    zone: [44, 18, 48, 18],
    dessin: (
      <>
        <path className="tr" d="M16 6 h84 l36 36 v114 q0 6 -6 6 h-114 q-6 0 -6 -6 v-144 q0 -6 6 -6 Z" />
        <path className="ti" d="M100 6 v28 q0 8 8 8 h28 Z" />
        <path className="lf" d="M28 128 h88M28 141 h64" />
      </>
    ),
  },
  enveloppe: {
    largeur: 188,
    hauteur: 126,
    zone: [52, 18, 12, 18],
    dessin: (
      <>
        <rect className="tr" x="6" y="8" width="176" height="110" rx="7" />
        <path className="ln" d="M10 14 L94 50 L178 14" />
      </>
    ),
  },
  calendrier: {
    largeur: 168,
    hauteur: 156,
    zone: [54, 16, 14, 16],
    dessin: (
      <>
        <rect className="tr" x="6" y="16" width="156" height="134" rx="9" />
        <path className="pl" d="M6 46 v-21 q0 -9 9 -9 h138 q9 0 9 9 v21 Z" />
        <path className="ln encre" d="M46 6 v20M122 6 v20" style={{ strokeWidth: 6 }} />
      </>
    ),
  },
  panier: {
    largeur: 188,
    hauteur: 136,
    zone: [56, 30, 14, 30],
    dessin: (
      <>
        <path className="ln" d="M48 48 C 48 4, 140 4, 140 48" />
        <path className="tr" d="M14 48 h160 l-16 78 q-1 6 -7 6 h-114 q-6 0 -7 -6 Z" />
        <path className="ti" d="M6 40 h176 q4 0 4 4 v8 q0 4 -4 4 h-176 q-4 0 -4 -4 v-8 q0 -4 4 -4 Z" />
      </>
    ),
  },
  outils: {
    largeur: 196,
    hauteur: 146,
    zone: [70, 16, 10, 16],
    dessin: (
      <>
        <path className="ln" d="M68 40 v-18 q0 -10 10 -10 h40 q10 0 10 10 v18" />
        <rect className="tr" x="6" y="40" width="184" height="100" rx="9" />
        <path className="ti" d="M6 50 q0 -10 10 -10 h164 q10 0 10 10 v14 h-184 Z" />
        <rect className="pl" x="88" y="57" width="20" height="13" rx="2" />
      </>
    ),
  },
  maison: {
    largeur: 184,
    hauteur: 156,
    zone: [76, 30, 14, 30],
    dessin: (
      <>
        <path className="ln" d="M132 42 v-28 h18 v42" />
        <path className="tr" d="M20 72 L92 14 L164 72 v72 q0 6 -6 6 h-132 q-6 0 -6 -6 Z" />
        <path className="ln" d="M6 78 L92 8 L178 78" />
      </>
    ),
  },
  billet: {
    largeur: 196,
    hauteur: 112,
    zone: [16, 46, 16, 46],
    dessin: (
      <>
        <rect className="tr" x="6" y="8" width="184" height="96" rx="7" />
        <rect className="lf" x="15" y="17" width="166" height="78" rx="4" style={{ strokeDasharray: '5 4', opacity: 0.6 }} />
        <circle className="ti" cx="34" cy="56" r="13" />
        <circle className="ti" cx="162" cy="56" r="13" />
      </>
    ),
  },
  horloge: {
    largeur: 156,
    hauteur: 156,
    zone: [44, 30, 30, 30],
    dessin: (
      <>
        <circle className="tr" cx="78" cy="78" r="70" />
        <path className="ln" d="M78 14 v9M78 133 v9M14 78 h9M133 78 h9" />
        <path className="ln" d="M78 28 v12 l9 6" />
      </>
    ),
  },
  nuage: {
    largeur: 196,
    hauteur: 126,
    zone: [46, 30, 20, 30],
    dessin: (
      <path
        className="tr"
        d="M42 110 C 14 110, 6 78, 30 68 C 24 40, 58 24, 80 40 C 90 12, 140 10, 146 42 C 176 36, 194 64, 176 84 C 194 104, 170 120, 150 110 Z"
      />
    ),
  },
  drapeau: {
    largeur: 176,
    hauteur: 156,
    zone: [18, 28, 70, 32],
    dessin: (
      <>
        <path className="ln encre" d="M20 150 V8" style={{ strokeWidth: 5 }} />
        <path className="tr" d="M22 12 C 62 0, 92 28, 164 12 L 150 52 L 164 92 C 92 106, 62 80, 22 92 Z" />
      </>
    ),
  },
};

/** Une forme de bulle est-elle un objet (sinon, l'une des huit papeteries du projet) ? */
export function estUnObjet(forme: FormeDeBulle | null | undefined): forme is ObjetDeBulle {
  return (OBJETS_DE_BULLE as readonly string[]).includes(forme ?? '');
}

/**
 * Ce qu'une bulle qui porte un objet pose sur elle, avec `data-objet` : sa taille et la zone de
 * son texte, à l'échelle dite (1 : les mesures de la maquette). `styles/bulles/objets.css` les lit.
 */
export function styleDUnObjet(objet: ObjetDeBulle, echelle = 1): CSSProperties {
  const { largeur, hauteur, zone } = OBJETS[objet];
  const [haut, droite, bas, gauche] = zone.map((v) => `${v * echelle}px`);
  return {
    '--objet-largeur': `${largeur * echelle}px`,
    '--objet-hauteur': `${hauteur * echelle}px`,
    '--objet-zone': `${haut} ${droite} ${bas} ${gauche}`,
  } as CSSProperties;
}

/**
 * Le dessin d'un objet, à poser dans la bulle qui le porte (un vrai élément : `::before` est le
 * calque du trait, `::after` le halo d'une bulle désignée). Il remplit la bulle, derrière son
 * texte ; le trait du projet (épaisseur, tremblé, transparence) et l'état de la bulle (couleur,
 * pointillé, papier) lui viennent des variables de `styles/bulles/objets.css`.
 */
export function SilhouetteDObjet({ objet }: { objet: ObjetDeBulle }) {
  const { largeur, hauteur, dessin } = OBJETS[objet];
  return (
    <svg
      className="silhouette-d-objet"
      viewBox={`0 0 ${largeur} ${hauteur}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <g className="silhouette-d-objet-trait">{dessin}</g>
    </svg>
  );
}

/**
 * Ce que la Carte et la Hiérarchie dessinent d'une bulle (chantier habit-des-bulles, modification
 * 03) : sa forme (l'une des huit) ou son objet, son trait, et la couleur choisie de sa branche
 * (`null` : la couleur d'axe automatique). Calculé par `habitsDessines` (`projets/habit.ts`) ;
 * au téléphone, rien (ni forme, ni trait, ni couleur) : il garde son dessin à lui.
 */
export interface HabitDessine {
  forme?: FormeDeProjet;
  objet?: ObjetDeBulle;
  trait?: TraitDeProjet;
  couleur: CouleurDeBulle | null;
}

/** Ce que l'habit dessiné lit d'une bulle : sa mère, sa couleur, et sa forme et son trait à elle s'ils sont connus. */
export type BulleAHabiller = BulleColoree & { forme?: FormeDeBulle | null; trait?: TraitDeProjet | null };

/** L'habit d'une bulle qu'on ne dessine pas : rien. */
export const SANS_HABIT_DESSINE: HabitDessine = Object.freeze({ couleur: null });

/**
 * Les attributs qu'une bulle de la Carte ou de la Hiérarchie pose sur elle pour son habit : sa
 * forme (`styles/bulles/<forme>.css`), son trait (`trait-<nom>.css`, la classe `trait-de-projet`
 * qui en porte les variables), son objet (`objets.css`, sa taille et sa zone).
 */
export function attributsDeLHabit(habit: HabitDessine): {
  classe: string;
  attributs: { 'data-forme'?: string; 'data-trait'?: string; 'data-objet'?: string };
  style: CSSProperties;
} {
  return {
    classe: habit.trait ? 'trait-de-projet' : '',
    attributs: {
      ...(habit.forme ? { 'data-forme': habit.forme } : {}),
      ...(habit.trait ? { 'data-trait': habit.trait } : {}),
      ...(habit.objet ? { 'data-objet': habit.objet } : {}),
    },
    style: habit.objet ? styleDUnObjet(habit.objet) : {},
  };
}
